<template>
  <div class="generator-review card p-4 mb-3" novalidate>
    <!-- Basic Information -->
    <h4>{{ $t("signup.se.generator_review.basic_information") }}</h4>

    <!-- Production Units -->
    <div
      v-for="(unit, index) in localGenerator.application_information
        .new_production_unit_applications"
      :key="index"
      class="border rounded p-3 mb-3"
    >
      <div class="d-flex justify-content-between align-items-center mb-3">
        <h5>
          {{ $t("signup.se.generator_review.production_unit") }} #{{
            Number(index) + 1
          }}
        </h5>
        <button
          v-if="index === 1"
          @click="removeProductionUnit(index)"
          class="btn btn-outline-danger btn-sm"
        >
          {{ $t("common.remove") }}
        </button>
      </div>

      <DynamicGeneratorField
        v-for="field in getProductionUnitFieldsForUnit(unit)"
        :key="field.title"
        :field="field"
        v-model="unit[field.model]"
        translationPrefix="signup.se"
      />
    </div>

    <div v-if="canAddMoreUnits" class="mb-4">
      <button @click="addProductionUnit" class="btn btn-outline-primary">
        {{ $t("common.add_another_unit") }}
      </button>
    </div>

    <!-- Metering Information -->
    <h4 class="mt-4">
      {{ $t("signup.se.generator_review.metering_information") }}
    </h4>
    <DynamicGeneratorField
      v-for="field in filteredGoBasicFields"
      :key="field.title"
      :field="field"
      v-model="
        localGenerator.application_information.go_application[field.model]
      "
      translationPrefix="signup.se"
    />

    <!-- Investment Support Section -->
    <template v-if="!conversionFromNationalToEECS">
      <h4 class="mt-4">
        {{ $t("signup.se.generator_review.investment_support") }}
      </h4>
      <div class="row mb-2 g-2">
        <label class="col-sm-12 pb-0 col-form-label">
          {{ $t("signup.se.generator_review.investment_support_question") }}
        </label>
        <div class="col-sm-11">
          <div
            class="form-check"
            v-for="option in investmentSupportOptions"
            :key="option.value"
          >
            <input
              class="form-check-input"
              type="radio"
              name="investment-support"
              :id="`investment-support-${slugify(option.value)}`"
              :value="option.value"
              v-model="
                localGenerator.application_information.go_application
                  .investment_support
              "
            />
            <label
              class="form-check-label"
              :for="`investment-support-${slugify(option.value)}`"
            >
              {{ $t(option.label) }}
            </label>
          </div>
        </div>
      </div>

      <!-- Investment Support Details -->
      <template
        v-if="
          localGenerator.application_information.go_application
            .investment_support === InvestmentSupport.YES
        "
      >
        <DynamicGeneratorField
          v-for="field in investmentFields"
          :key="field.title"
          :field="field"
          v-model="
            localGenerator.application_information.go_application[field.model]
          "
          translationPrefix="signup.se"
        />
      </template>
    </template>
  </div>
</template>

<script setup lang="ts">
import DynamicGeneratorField from "@/signup/DynamicGeneratorField.vue";
import type { Account, Generator } from "@/types";
import { computed, reactive, toRef, watch } from "vue";
import { useI18n } from "vue-i18n";
import {
  CorrectionFactor,
  FlowType,
  InvestmentSupport,
  MeteringLocation,
  RegistrationStatus,
} from "./enums";
import { getGeneratorFields, getGoBasicFields } from "./generator-fields";
import type {
  SeGoApplication,
  SeProductionUnit,
  SeProductionUnitNewDevice,
} from "./types";

const props = withDefaults(
  defineProps<{
    generatorIndex: number;
    companyIndex: number;
    accountData: Account;
    conversionFromNationalToEECS?: boolean;
  }>(),
  { conversionFromNationalToEECS: false }
);

const emit = defineEmits<{
  handleUpdateGenerator: [generator: Generator];
}>();

const { t } = useI18n();

const generator = computed(() => {
  return props.accountData.companies[props.companyIndex].generators[
    props.generatorIndex
  ];
});

const createEmptyUnit = () => {
  if (props.conversionFromNationalToEECS) {
    return {
      unit_type: "conversion",
      device_name: "",
      device_power: null,
      technology: null,
      property_designation: "",
      estimated_annual_production: null,
      thermal_subtype: null,
      thermal_is_chp: null,
    };
  }
  return {
    unit_type: "new_device",
    device_name: "",
    device_power: null,
    commissioning_date: "",
    property_designation: "",
    postal_code: "",
    city: "",
    technology: null,
    estimated_annual_production: null,
    thermal_subtype: null,
    thermal_is_chp: null,
  };
};

const cleanProductionUnit = (unit: SeProductionUnit): SeProductionUnit => {
  const baseFields = {
    device_name: unit.device_name,
    device_power: unit.device_power,
    technology: unit.technology,
    property_designation: unit.property_designation,
    estimated_annual_production: unit.estimated_annual_production,
    thermal_subtype: unit.thermal_subtype,
    thermal_is_chp: unit.thermal_is_chp,
  };

  if (props.conversionFromNationalToEECS) {
    return { unit_type: "conversion", ...baseFields };
  }

  const newDeviceUnit = unit as SeProductionUnitNewDevice;
  return {
    unit_type: "new_device",
    ...baseFields,
    commissioning_date: newDeviceUnit.commissioning_date,
    postal_code: newDeviceUnit.postal_code,
    city: newDeviceUnit.city,
  };
};

const cleanGoApplication = (
  goApplication: SeGoApplication | undefined
): Partial<SeGoApplication> => {
  if (!goApplication) {
    return {};
  }

  if (props.conversionFromNationalToEECS) {
    // For conversion flow, exclude non-EECS fields
    const {
      grid_area_id: _grid_area_id,
      grid_eidel_id: _grid_eidel_id,
      grid_metered_by_hour: _grid_metered_by_hour,
      grid_concession: _grid_concession,
      investment_support: _investment_support,
      investment_support_amount: _investment_support_amount,
      investment_support_date: _investment_support_date,
      grid_owner_name: _grid_owner_name,
      grid_owner_corporate_id: _grid_owner_corporate_id,
      ...keptFields
    } = goApplication;

    return keptFields;
  } else {
    // For new device flow, ensure all fields are present (keep existing data)
    return goApplication;
  }
};

// Initialize with default values
const localGenerator = reactive({
  ...generator.value,
  application_information: {
    ...generator.value.application_information,
    new_production_unit_applications: [
      // Clean existing units or create a new one if none exist
      ...(generator.value.application_information?.new_production_unit_applications?.map(
        cleanProductionUnit
      ) || []),
      // If no units exist, create one empty unit
      ...(!generator.value.application_information
        ?.new_production_unit_applications?.length
        ? [createEmptyUnit()]
        : []),
    ],
    go_application: {
      // Defaults first (fallback values)
      metering_location: MeteringLocation.DIRECTLY_BEFORE_GRID_CONNECTION,
      holding_percentage: 100.0,
      // Only set excluded fields for non-conversion flow
      ...(!props.conversionFromNationalToEECS && {
        investment_support: InvestmentSupport.NO,
        investment_support_amount: null,
        investment_support_date: null,
        grid_metered_by_hour: true,
        grid_concession: true,
      }),
      // Spread existing user data (overrides defaults)
      ...cleanGoApplication(
        generator.value.application_information?.go_application
      ),
      // Enforce correction_factor default (after spreading to prevent null override)
      correction_factor:
        cleanGoApplication(
          generator.value.application_information?.go_application
        )?.correction_factor ?? CorrectionFactor.STANDARD_VALUE,
      // Enforce flow-specific fields last
      registration_status: props.conversionFromNationalToEECS
        ? RegistrationStatus.REGISTERED
        : RegistrationStatus.NOT_REGISTERED,
    },
    flow_type:
      generator.value.application_information?.flow_type ||
      (props.conversionFromNationalToEECS
        ? FlowType.CONVERSION
        : FlowType.NEW_DEVICE),
  },
});

const goBasicFields = getGoBasicFields(
  t,
  localGenerator.application_information.go_application
);

const getProductionUnitFieldsForUnit = (unit: SeProductionUnit) => {
  const unitTechnology = toRef(unit, "technology");
  const { productionUnitFields, thermalFields } = getGeneratorFields(
    t,
    unitTechnology
  );

  // Start with base production unit fields
  let fields = [...productionUnitFields];

  // Insert thermal fields right after technology field (index 0)
  fields.splice(1, 0, ...thermalFields);

  // Filter out conversion-excluded fields
  if (props.conversionFromNationalToEECS) {
    const excludedFields = ["commissioning_date", "postal_code", "city"];
    fields = fields.filter((field) => !excludedFields.includes(field.model));
  }

  return fields;
};

const filteredGoBasicFields = computed(() => {
  if (props.conversionFromNationalToEECS) {
    const excludedFields = [
      "grid_area_id",
      "grid_eidel_id",
      "grid_metered_by_hour",
      "grid_concession",
    ];
    return goBasicFields.filter(
      (field) => !excludedFields.includes(field.model)
    );
  }
  return goBasicFields;
});

const canAddMoreUnits = computed(() => {
  return (
    localGenerator.application_information.new_production_unit_applications
      .length < 2
  );
});

const addProductionUnit = () => {
  if (canAddMoreUnits.value) {
    localGenerator.application_information.new_production_unit_applications.push(
      createEmptyUnit()
    );
  }
};

const removeProductionUnit = (index: number) => {
  if (index > 0) {
    localGenerator.application_information.new_production_unit_applications.splice(
      index,
      1
    );
  }
};

watch(
  localGenerator,
  (newValue) => {
    emit("handleUpdateGenerator", newValue);
  },
  { deep: true }
);

const { investmentFields, investmentSupportOptions } = getGeneratorFields(t);

const slugify = (str: string) => {
  return String(str).toLowerCase().replace(/\s+/g, "-");
};
</script>

<style scoped lang="scss">
.generator-review {
  .col-form-label {
    text-align: left;
    font-weight: bold;
    padding: 0;
  }

  .form-check {
    margin-bottom: 0.5rem;
  }

  h4 {
    margin-bottom: 1rem;
  }

  .border {
    border-color: #dee2e6 !important;
  }
}

@media (max-width: 575.98px) {
  .generator-review {
    .col-form-label {
      margin-top: 1rem;
    }
  }
  h4 {
    font-size: 1.2rem;
  }
}
</style>
